import {Palette} from 'hubbl-shared';
import {memo, useCallback, useState, useRef, useEffect} from 'react';
import styled from 'styled-components';
import {TextHeading5, TextHeading7} from '../index';
import {useI18n} from '../../hooks';

export interface FilterState {
  showDeclined: boolean;
  showAccepted: boolean;
  showPending: boolean;
  showEvents: boolean;
  showGroups: boolean;
  showConnects: boolean;
  showMyOwn: boolean;
  searchString: string;
}

export interface FilterCounts {
  pending: number;
  accepted: number;
  declined: number;
  events: number;
  groups: number;
  connects: number;
  myOwn: number;
}

interface SocialActivitiesFiltersProps {
  filters: FilterState;
  onFiltersChange: (filters: FilterState) => void;
  counts?: FilterCounts;
}

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
  z-index: 100;
  border-radius: 12px;
  box-shadow: 0 2px 8px ${Palette.almostBlackTransparent10};
  transition: all 0.3s ease;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const FiltersContainer = styled.div`
  background: ${Palette.white};
  border-radius: 12px;
  padding: 0.75rem 1rem;
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${Palette.gradient1Color1},
      ${Palette.gradient1Color2}
    );
    border-radius: 12px 12px 0 0;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    gap: 0.75rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    padding: 0.5rem 0.75rem 0.75rem 0.75rem;
    gap: 0.5rem;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  align-items: center;
  justify-content: flex-start;
  flex: 1;

  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const FilterDivider = styled.div`
  width: 1px;
  height: 16px;
  background: ${Palette.almostBlack250};
  margin: 0 0.25rem;
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FiltersTitleContainer = styled.div`
  margin-right: 0.75rem;
  margin-bottom: 0;
  flex-shrink: 0;
  white-space: nowrap;

  @media (max-width: 480px) {
    margin-right: 0.5rem;
  }
`;

const SearchContainer = styled.div<{$isExpanded: boolean}>`
  position: relative;
  margin-left: ${props => (props.$isExpanded ? '0.75rem' : '0.5rem')};
  margin-right: 0.75rem;
  margin-bottom: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;

  transition: all 0.3s ease;
  width: ${props => (props.$isExpanded ? '250px' : 'auto')};

  @media (max-width: 768px) {
    width: ${props => (props.$isExpanded ? '200px' : 'auto')};
    margin-right: 0.5rem;
  }

  @media (max-width: 480px) {
    width: ${props => (props.$isExpanded ? '100%' : 'auto')};
    margin-left: 0;
    margin-right: 0;
    margin-bottom: 0.75rem;
  }
`;

const SearchIconButton = styled.button<{$isExpanded: boolean}>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border: none;
  background: ${Palette.primarySubtleGreen};
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  opacity: ${props => (props.$isExpanded ? 0 : 1)};
  transform: scale(${props => (props.$isExpanded ? 0.8 : 1)});
  pointer-events: ${props => (props.$isExpanded ? 'none' : 'auto')};
  z-index: 1;
  white-space: nowrap;

  &:hover {
    background: ${Palette.primaryCherokee}20;
  }

  svg {
    width: 14px;
    height: 14px;
    color: ${Palette.almostBlack500};
    flex-shrink: 0;
  }

  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
  }

  @media (max-width: 480px) {
    padding: 0.2rem 0.4rem;
  }
`;

const SearchInputWrapper = styled.div<{$isExpanded: boolean}>`
  position: absolute;
  left: 0;
  right: 0;
  opacity: ${props => (props.$isExpanded ? 1 : 0)};
  transition: all 0.3s ease;
  pointer-events: ${props => (props.$isExpanded ? 'auto' : 'none')};
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
`;

const SearchInput = styled.input`
  width: 100%;

  padding: 0.375rem 2.5rem 0.375rem 1rem;
  border: 1px solid ${Palette.almostBlackTransparent5};
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${Palette.almostBlack};
  background: ${Palette.white};

  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${Palette.primaryCherokee};
    box-shadow: 0 0 0 3px ${Palette.primaryCherokee}20;
  }

  &::placeholder {
    color: ${Palette.almostBlackTransparent40};
  }

  @media (max-width: 768px) {
    padding: 0.25rem 2.25rem 0.25rem 0.75rem;
    font-size: 0.65rem;
  }

  @media (max-width: 480px) {
    padding: 0.2rem 2rem 0.2rem 0.625rem;
    font-size: 0.6rem;
  }
`;

const SearchCloseButton = styled.button`
  position: absolute;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 50%;
  transition: all 0.2s ease;

  &:hover {
    background: ${Palette.almostBlackTransparent5};
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${Palette.almostBlack500};
  }
`;

const FilterGroupLabel = styled.div`
  margin-right: 0.375rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  white-space: nowrap;

  @media (max-width: 480px) {
    margin-right: 0.25rem;
  }
`;

const FilterToggle = styled.button<{
  active: boolean;
  variant: 'status' | 'type';
}>`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  border: 0px;
  flex-shrink: 0;
  white-space: nowrap;

  background: ${props =>
    props.active
      ? props.variant === 'status'
        ? Palette.primaryTerraCotta + '80'
        : Palette.primaryCherokee + '80'
      : Palette.primarySubtleGreen};
  color: ${props =>
    props.active
      ? props.variant === 'status'
        ? Palette.primaryTerraCotta
        : Palette.primaryCherokee
      : Palette.almostBlack500};
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  font-size: 0.75rem;

  &:hover {
    border-color: ${props =>
      props.variant === 'status'
        ? Palette.primaryTerraCotta
        : Palette.primaryCherokee};
    background: ${props =>
      props.active
        ? props.variant === 'status'
          ? Palette.primaryTerraCotta + '30'
          : Palette.primaryCherokee + '30'
        : Palette.almostBlackTransparent5};
  }

  @media (max-width: 768px) {
    padding: 0.25rem 0.5rem;
    font-size: 0.65rem;
  }

  @media (max-width: 480px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.6rem;
    gap: 0.2rem;
  }
`;

const FilterIcon = styled.span`
  font-size: 0.75rem;
  line-height: 1;
`;

const FilterBadge = styled.span<{active: boolean}>`
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background: ${props =>
    props.active ? Palette.primaryTerraCotta : 'transparent'};
  transition: all 0.2s ease;
`;

const OwnershipToggleContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: auto;

  @media (max-width: 480px) {
    margin-left: 0;
    align-self: flex-start;
  }
`;

const OwnershipToggle = styled.div`
  display: flex;
  background: ${Palette.almostBlackTransparent5};
  border: 1px solid ${Palette.almostBlackTransparent5};
  border-radius: 6px;
  overflow: hidden;
  position: relative;
`;

const OwnershipOption = styled.button<{$isActive: boolean}>`
  padding: 0.25rem 0.625rem;
  font-size: 0.7rem;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props =>
    props.$isActive
      ? `linear-gradient(135deg, ${Palette.gradient1Color1} 0%, ${Palette.gradient1Color2} 100%)`
      : 'transparent'};
  position: relative;
  z-index: 1;
  color: ${props => (props.$isActive ? Palette.white : Palette.almostBlack500)};

  &:hover {
    background: ${props =>
      props.$isActive
        ? `linear-gradient(135deg, ${Palette.gradient1Color1}80 0%, ${Palette.gradient1Color2}80 100%)`
        : Palette.almostBlackTransparent10};
  }

  @media (max-width: 768px) {
    padding: 0.2rem 0.5rem;
    font-size: 0.6rem;
  }
`;

export const SocialActivitiesFilters = memo(
  ({filters, onFiltersChange, counts}: SocialActivitiesFiltersProps) => {
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const searchInputRef = useRef<HTMLInputElement>(null);
    const t = useI18n();

    const handleToggle = useCallback(
      (key: keyof FilterState) => {
        onFiltersChange({
          ...filters,
          [key]: !filters[key],
        });
      },
      [filters, onFiltersChange],
    );

    const handleSearchIconClick = useCallback(() => {
      setIsSearchExpanded(true);
      // Focus input after animation starts
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }, []);

    const handleCloseSearch = useCallback(() => {
      setIsSearchExpanded(false);
      onFiltersChange({...filters, searchString: ''});
    }, [filters, onFiltersChange]);

    // Auto-expand if there's a search string
    useEffect(() => {
      if (filters.searchString) {
        setIsSearchExpanded(true);
      }
    }, [filters.searchString]);

    return (
      <StickyContainer>
        <FiltersContainer>
          <FilterGroup>
            <FiltersTitleContainer>
              <TextHeading5 text={t.common.filter} />
            </FiltersTitleContainer>
            <FilterGroupLabel>
              <TextHeading7
                text={t.filters.status}
                color={Palette.almostBlack500}
              />
            </FilterGroupLabel>
            <FilterToggle
              active={filters.showPending}
              onClick={() => handleToggle('showPending')}
              variant="status">
              <FilterIcon>⏳</FilterIcon>
              <TextHeading7
                text={`${t.filters.pending}${counts ? ` (${counts.pending})` : ''}`}
              />
              <FilterBadge active={filters.showPending} />
            </FilterToggle>
            <FilterToggle
              active={filters.showAccepted}
              onClick={() => handleToggle('showAccepted')}
              variant="status">
              <FilterIcon>✓</FilterIcon>
              <TextHeading7
                text={`${t.filters.accepted}${counts ? ` (${counts.accepted})` : ''}`}
              />
              <FilterBadge active={filters.showAccepted} />
            </FilterToggle>
            <FilterToggle
              active={filters.showDeclined}
              onClick={() => handleToggle('showDeclined')}
              variant="status">
              <FilterIcon>✗</FilterIcon>
              <TextHeading7
                text={`${t.filters.declined}${counts ? ` (${counts.declined})` : ''}`}
              />
              <FilterBadge active={filters.showDeclined} />
            </FilterToggle>
            <FilterDivider />
            <FilterGroupLabel>
              <TextHeading7
                text={t.filters.types}
                color={Palette.almostBlack500}
              />
            </FilterGroupLabel>
            <FilterToggle
              active={filters.showEvents}
              onClick={() => handleToggle('showEvents')}
              variant="type">
              <FilterIcon>🎤</FilterIcon>
              <TextHeading7
                text={`${t.filters.events}${counts ? ` (${counts.events})` : ''}`}
              />
              <FilterBadge active={filters.showEvents} />
            </FilterToggle>
            <FilterToggle
              active={filters.showGroups}
              onClick={() => handleToggle('showGroups')}
              variant="type">
              <FilterIcon>👥</FilterIcon>
              <TextHeading7
                text={`${t.filters.groups}${counts ? ` (${counts.groups})` : ''}`}
              />
              <FilterBadge active={filters.showGroups} />
            </FilterToggle>
            <FilterToggle
              active={filters.showConnects}
              onClick={() => handleToggle('showConnects')}
              variant="type">
              <FilterIcon>🤝</FilterIcon>
              <TextHeading7
                text={`${t.filters.connects}${counts ? ` (${counts.connects})` : ''}`}
              />
              <FilterBadge active={filters.showConnects} />
            </FilterToggle>
            <SearchContainer $isExpanded={isSearchExpanded}>
              <SearchIconButton
                $isExpanded={isSearchExpanded}
                onClick={handleSearchIconClick}
                aria-label="Search">
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <TextHeading7
                  text={t.filters.search}
                  color={Palette.almostBlack500}
                />
              </SearchIconButton>
              <SearchInputWrapper $isExpanded={isSearchExpanded}>
                <SearchInput
                  ref={searchInputRef}
                  type="text"
                  placeholder={t.filters.searchPlaceholder}
                  value={filters.searchString}
                  onChange={e =>
                    onFiltersChange({...filters, searchString: e.target.value})
                  }
                />
                <SearchCloseButton
                  onClick={handleCloseSearch}
                  aria-label="Close search">
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18 6L6 18M6 6L18 18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </SearchCloseButton>
              </SearchInputWrapper>
            </SearchContainer>
          </FilterGroup>
          <OwnershipToggleContainer>
            <OwnershipToggle>
              <OwnershipOption
                $isActive={!filters.showMyOwn}
                onClick={() => {
                  if (filters.showMyOwn) {
                    handleToggle('showMyOwn');
                  }
                }}>
                <TextHeading7 text={t.filters.all} />
              </OwnershipOption>
              <OwnershipOption
                $isActive={filters.showMyOwn}
                onClick={() => {
                  if (!filters.showMyOwn) {
                    handleToggle('showMyOwn');
                  }
                }}>
                <TextHeading7
                  text={`${t.filters.myOwn}${counts ? ` (${counts.myOwn})` : ''}`}
                />
              </OwnershipOption>
            </OwnershipToggle>
          </OwnershipToggleContainer>
        </FiltersContainer>
      </StickyContainer>
    );
  },
);
